import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { onMobileNavToggle } from 'store/slices/themeSlice';
import { useDispatch } from 'react-redux';

const MobileNavigationContext = createContext();

const initialState = {
  isOpen: false,
  activeDropdown: null,
  isMobile: false,
  isLoading: false
};

const mobileNavReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MOBILE':
      return { ...state, isMobile: action.payload };
    case 'TOGGLE_NAV':
      return { ...state, isOpen: !state.isOpen };
    case 'CLOSE_NAV':
      return { ...state, isOpen: false };
    case 'OPEN_NAV':
      return { ...state, isOpen: true };
    case 'SET_ACTIVE_DROPDOWN':
      return { ...state, activeDropdown: action.payload };
    case 'CLOSE_DROPDOWN':
      return { ...state, activeDropdown: null };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

export const MobileNavigationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(mobileNavReducer, initialState);
  const reduxDispatch = useDispatch();

  const toggleNav = useCallback(() => {
    dispatch({ type: 'TOGGLE_NAV' });
    reduxDispatch(onMobileNavToggle(!state.isOpen));
  }, [state.isOpen, reduxDispatch]);

  const closeNav = useCallback(() => {
    dispatch({ type: 'CLOSE_NAV' });
    reduxDispatch(onMobileNavToggle(false));
  }, [reduxDispatch]);

  const openNav = useCallback(() => {
    dispatch({ type: 'OPEN_NAV' });
    reduxDispatch(onMobileNavToggle(true));
  }, [reduxDispatch]);

  const setActiveDropdown = useCallback((key) => {
    dispatch({ type: 'SET_ACTIVE_DROPDOWN', payload: key });
  }, []);

  const closeDropdown = useCallback(() => {
    dispatch({ type: 'CLOSE_DROPDOWN' });
  }, []);

  const setMobile = useCallback((isMobile) => {
    dispatch({ type: 'SET_MOBILE', payload: isMobile });
  }, []);

  const setLoading = useCallback((isLoading) => {
    dispatch({ type: 'SET_LOADING', payload: isLoading });
  }, []);

  const value = {
    ...state,
    toggleNav,
    closeNav,
    openNav,
    setActiveDropdown,
    closeDropdown,
    setMobile,
    setLoading
  };

  return (
    <MobileNavigationContext.Provider value={value}>
      {children}
    </MobileNavigationContext.Provider>
  );
};

export const useMobileNavigationContext = () => {
  const context = useContext(MobileNavigationContext);
  if (!context) {
    throw new Error('useMobileNavigationContext must be used within a MobileNavigationProvider');
  }
  MobileNavigationProvider.propTypes = {
    children: PropTypes.node.isRequired,
  };
  return context;
}; 